import type { ShiftDashboardFilterOptions, ShiftDashboardView, ShiftViewPayload } from '@/api/shiftDashboardApi'
import { MAX_DASHBOARD_NAME_LENGTH } from './dashboardLayouts'
import { tileIdsToWidgets, viewTileIds } from './shiftTiles'

// ─── Shift Dashboard filters and views (z8nrz7fbcj / z8nrz7fbcm) ─────────────
//
// The filters ride in the URL so a refresh, a link or the back button reopens
// the same shift. Opening a named view writes ITS filters into the URL; the
// user may then narrow further for this visit without changing the view.
//
// Pure, so the rules are unit-tested (vitest here runs `.ts` only).

export interface ShiftFilters {
  siteId: string | null
  roomId: string | null
  /** Tenant calendar day, YYYY-MM-DD. Never stored on a view: a view is for any day. */
  date: string
  shiftDefinitionId: string | null
  teamId: string | null
  groupId: string | null
}

/** The filters a view stores — everything but the day. */
export type ShiftViewFilters = Omit<ShiftFilters, 'date'>

const PARAM: Record<keyof ShiftFilters, string> = {
  siteId: 'site',
  roomId: 'room',
  date: 'date',
  shiftDefinitionId: 'shift',
  teamId: 'team',
  groupId: 'group',
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** Filters from the URL. A missing or malformed day is `today`. */
export function filtersFromParams(params: URLSearchParams, today: string): ShiftFilters {
  const id = (k: keyof ShiftFilters) => params.get(PARAM[k]) || null
  const date = params.get(PARAM.date)
  return {
    siteId: id('siteId'),
    roomId: id('roomId'),
    date: date && ISO_DAY.test(date) ? date : today,
    shiftDefinitionId: id('shiftDefinitionId'),
    teamId: id('teamId'),
    groupId: id('groupId'),
  }
}

/**
 * Writes the filters into `params` (a copy), leaving every other param (the
 * view id) alone. Today's date is left out so a plain link always means
 * "now", not the day it was copied.
 */
export function filtersToParams(filters: ShiftFilters, params: URLSearchParams, today: string): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const k of Object.keys(PARAM) as (keyof ShiftFilters)[]) {
    const v = filters[k]
    if (v && !(k === 'date' && v === today)) next.set(PARAM[k], v)
    else next.delete(PARAM[k])
  }
  return next
}

/** The groups the Group filter offers: the team's own when a team is chosen, else all. */
export function groupsForTeam(
  options: Pick<ShiftDashboardFilterOptions, 'teams' | 'groups'>,
  teamId: string | null,
): ShiftDashboardFilterOptions['groups'] {
  if (!teamId) return options.groups
  const team = options.teams.find((t) => t.id === teamId)
  if (!team) return []
  const inTeam = new Set(team.groupIds)
  return options.groups.filter((g) => inTeam.has(g.id))
}

/** The rooms the Room filter offers: the site's own when a site is chosen, else all. */
export function roomsForSite(
  rooms: ShiftDashboardFilterOptions['rooms'],
  siteId: string | null,
): ShiftDashboardFilterOptions['rooms'] {
  return siteId ? rooms.filter((r) => r.siteId === siteId) : rooms
}

/**
 * Makes the filters consistent with what exists: an id the options no longer
 * hold (deleted, another tenant's link) is dropped, a group outside the chosen
 * team and a room outside the chosen site are cleared. Returns the same object
 * when nothing changes, so it is safe to call on every render.
 */
export function reconcileFilters<F extends ShiftViewFilters>(filters: F, options: ShiftDashboardFilterOptions): F {
  const has = (list: { id: string }[], id: string | null) => !id || list.some((x) => x.id === id)
  const siteId = has(options.sites, filters.siteId) ? filters.siteId : null
  const roomId = has(roomsForSite(options.rooms, siteId), filters.roomId) ? filters.roomId : null
  const shiftDefinitionId = has(options.shifts, filters.shiftDefinitionId) ? filters.shiftDefinitionId : null
  const teamId = has(options.teams, filters.teamId) ? filters.teamId : null
  const groupId = has(groupsForTeam(options, teamId), filters.groupId) ? filters.groupId : null
  const same =
    siteId === filters.siteId &&
    roomId === filters.roomId &&
    shiftDefinitionId === filters.shiftDefinitionId &&
    teamId === filters.teamId &&
    groupId === filters.groupId
  return same ? filters : { ...filters, siteId, roomId, shiftDefinitionId, teamId, groupId }
}

/**
 * One filter changed, with what it implies for the others: a new site clears a
 * room outside it, a new team clears a group outside it. Picking a room or a
 * group fills in its site or team when that is unambiguous.
 */
export function setFilter(
  filters: ShiftFilters,
  key: keyof ShiftFilters,
  value: string | null,
  options: ShiftDashboardFilterOptions,
): ShiftFilters {
  const next: ShiftFilters = { ...filters, [key]: key === 'date' ? value ?? filters.date : value }
  if (key === 'roomId' && value && !next.siteId) {
    next.siteId = options.rooms.find((r) => r.id === value)?.siteId ?? null
  }
  if (key === 'groupId' && value && !next.teamId) {
    const teams = options.teams.filter((t) => t.groupIds.includes(value))
    if (teams.length === 1) next.teamId = teams[0].id
  }
  return reconcileFilters(next, options)
}

export function viewFilters(view: ShiftDashboardView): ShiftViewFilters {
  return {
    siteId: view.siteId,
    roomId: view.roomId,
    shiftDefinitionId: view.shiftDefinitionId,
    teamId: view.teamId,
    groupId: view.groupId,
  }
}

/** Whether the filters on screen differ from the open view's (the day aside). */
export function filtersDifferFromView(filters: ShiftFilters, view: ShiftDashboardView): boolean {
  const stored = viewFilters(view)
  return (Object.keys(stored) as (keyof ShiftViewFilters)[]).some((k) => stored[k] !== filters[k])
}

/** The tiles a view shows, in order; the default set when it has none (or no view is open). */
export function viewTiles(view: ShiftDashboardView | null): string[] {
  return viewTileIds(view?.widgets ?? [])
}

/** The body that saves the on-screen filters and tiles into a view. */
export function viewPayload(name: string, filters: ShiftFilters, tileIds: string[]): ShiftViewPayload {
  return {
    name: name.trim(),
    siteId: filters.siteId,
    roomId: filters.roomId,
    shiftDefinitionId: filters.shiftDefinitionId,
    teamId: filters.teamId,
    groupId: filters.groupId,
    widgets: tileIdsToWidgets(tileIds),
  }
}

/**
 * "North Campus · Suite 1 · 15 Oct · DAY · Micro / Micro-1": what the open
 * tile is scoped to, in words, for the drill-down header.
 */
export function scopeLabel(
  filters: ShiftFilters,
  options: Pick<ShiftDashboardFilterOptions, 'sites' | 'rooms' | 'shifts' | 'teams' | 'groups'> | undefined,
): string {
  return scopeParts(filters, options).join(' · ')
}

/** The same scope as separate parts, for rendering as chips. */
export function scopeParts(
  filters: ShiftFilters,
  options: Pick<ShiftDashboardFilterOptions, 'sites' | 'rooms' | 'shifts' | 'teams' | 'groups'> | undefined,
): string[] {
  const name = <T extends { id: string }>(list: T[] | undefined, id: string | null, label: (x: T) => string) => {
    const found = id ? list?.find((x) => x.id === id) : undefined
    return found ? label(found) : null
  }
  const [y, m, d] = filters.date.split('-').map(Number)
  const day = new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  const team = name(options?.teams, filters.teamId, (t) => t.name)
  const group = name(options?.groups, filters.groupId, (g) => g.name)
  return [
    name(options?.sites, filters.siteId, (s) => s.name) ?? 'All sites',
    name(options?.rooms, filters.roomId, (r) => r.label) ?? 'All rooms',
    day,
    name(options?.shifts, filters.shiftDefinitionId, (s) => s.code) ?? 'Whole day',
    team && group ? `${team} / ${group}` : (group ?? team ?? 'All teams'),
  ]
}

/** Null when the name is fine; otherwise why not. Case-insensitive, like the 409 it saves a trip for. */
export function validateViewName(
  name: string,
  views: Pick<ShiftDashboardView, 'id' | 'name'>[],
  editingId: string | null,
): string | null {
  const trimmed = name.trim()
  if (!trimmed) return 'Enter a name.'
  if (trimmed.length > MAX_DASHBOARD_NAME_LENGTH) return `Use at most ${MAX_DASHBOARD_NAME_LENGTH} characters.`
  const lower = trimmed.toLowerCase()
  if (views.some((v) => v.id !== editingId && v.name.trim().toLowerCase() === lower)) {
    return `A view named "${trimmed}" already exists.`
  }
  return null
}
